import * as React from 'react';
import type { DropResult } from 'react-beautiful-dnd';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import { JobTrackingTemplateColumnType } from '@acceligentllc/shared/enums/jobTrackingTemplateColumnType';

import { Button } from '@acceligentllc/storybook';

import CustomModal from 'af-components/CustomModal';

import styles from './styles.module.scss';
import type { TemplateColumnFM } from '../Shared/formModel';

interface Props {
	show: boolean;
	onClose: () => void;
	columns: TemplateColumnFM[];
	onSave: (reordered: TemplateColumnFM[]) => void;
}

const ReorderColumnsModal: React.FC<Props> = ({ show, onClose, columns, onSave }) => {
	const [dateColumn, setDateColumn] = React.useState<Nullable<TemplateColumnFM>>(null);
	const [localColumns, setLocalColumns] = React.useState<TemplateColumnFM[]>([]);
	const [initialOrderKey, setInitialOrderKey] = React.useState<string>('');

	React.useEffect(() => {
		if (show) {
			const sorted = [...(columns ?? [])].sort((a, b) => {
				return (a.index ?? 0) - (b.index ?? 0);
			});

			const fixedDateColumn = sorted.find((column) => {
				return column.type === JobTrackingTemplateColumnType.DATE;
			}) ?? null;

			const reorderableColumns = sorted.filter((column) => {
				return column.type !== JobTrackingTemplateColumnType.DATE;
			});

			setDateColumn(fixedDateColumn);
			setLocalColumns(reorderableColumns);
			setInitialOrderKey(reorderableColumns.map((column) => column._cid).join('|'));
			return;
		}

		setDateColumn(null);
		setLocalColumns([]);
		setInitialOrderKey('');
	}, [show, columns]);

	const reorder = React.useCallback((fromIndex: number, toIndex: number) => {
		setLocalColumns((prev) => {
			const next = [...prev];
			const [moved] = next.splice(fromIndex, 1);

			if (!moved) {
				return prev;
			}

			next.splice(toIndex, 0, moved);
			return next;
		});
	}, []);

	const onDragEnd = React.useCallback(({ source, destination }: DropResult) => {
		if (!destination) {
			return;
		}

		if (source.index === destination.index) {
			return;
		}

		reorder(source.index, destination.index);
	}, [reorder]);

	const hasChanges = React.useMemo(() => {
		if (!show) {
			return false;
		}

		return localColumns.map((column) => column._cid).join('|') !== initialOrderKey;
	}, [show, localColumns, initialOrderKey]);

	const handleSave = React.useCallback(() => {
		const finalColumns = [
			...(dateColumn ? [dateColumn] : []),
			...localColumns,
		].map((column, idx) => {
			return {
				...column,
				index: idx + 1,
			};
		});

		onSave(finalColumns);
		onClose();
	}, [dateColumn, localColumns, onSave, onClose]);

	const renderFixedDateRow = React.useCallback((column: TemplateColumnFM) => {
		return (
			<div className={styles.block} key={column._cid}>
				<div className={styles['block-left']}>
					<div className={styles['block-index']}>
						1
					</div>

					<span className="icon-lock" />

					<div className={styles['block-name']}>
						{column.name || '(Untitled column)'}
					</div>
				</div>
			</div>
		);
	}, []);

	const renderRow = React.useCallback((column: TemplateColumnFM, idx: number) => {
		const draggableId = `column-${column._cid}`;
		const displayIndex = idx + 2;

		return (
			<Draggable draggableId={draggableId} index={idx} key={draggableId}>
				{(provided) => (
					<div
						ref={provided.innerRef}
						{...provided.draggableProps}
						className={styles.block}
						style={provided.draggableProps.style}
					>
						<div className={styles['block-left']}>
							<div className={styles['block-index']}>
								{displayIndex}
							</div>

							<div className={styles['block-drag-handle']} {...provided.dragHandleProps}>
								<span className="icon-drag_indicator" />
							</div>

							<div className={styles['block-name']}>
								{column.name || '(Untitled column)'}
							</div>
						</div>
					</div>
				)}
			</Draggable>
		);
	}, []);

	const renderList = React.useCallback(() => {
		return (
			<Droppable direction="vertical" droppableId="template-column-reorder">
				{(droppableProvided) => (
					<div
						ref={droppableProvided.innerRef}
						{...droppableProvided.droppableProps}
						className={styles.container}
					>
						{dateColumn ? renderFixedDateRow(dateColumn) : null}
						{localColumns.map(renderRow)}
						{droppableProvided.placeholder}
					</div>
				)}
			</Droppable>
		);
	}, [dateColumn, localColumns, renderFixedDateRow, renderRow]);

	return (
		<CustomModal closeModal={onClose} modalStyle="info" showModal={show} size="lg">
			<CustomModal.Header closeModal={onClose} title="Rearrange Columns" />
			<CustomModal.Body padding="none">
				<DragDropContext onDragEnd={onDragEnd}>
					{renderList()}
				</DragDropContext>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button label="Close" onClick={onClose} style="secondary" />
				<Button disabled={!hasChanges} label="Save" onClick={handleSave} style="primary" />
			</CustomModal.Footer>
		</CustomModal>
	);
};

export default React.memo(ReorderColumnsModal);
